import _ from 'lodash'
import { Artwork, Auth, FrameType, Sale, Size, Tag, User } from 'api'
import { normalize } from 'normalizr'
import { reset } from 'redux-form'

import { stringToBase64 } from 'lib/converter'
import * as actionTypes from 'redux/actionTypes'

import * as schema from './schema'

// action creators

export const artworkPageChange = (page) => ({
  type: actionTypes.ARTWORK_PAGE_CHANGE, page
})

export const artworkRowsPerPageChange = (rowsPerPage) => ({
  type: actionTypes.ARTWORK_ROWS_PER_PAGE_CHANGE, rowsPerPage
})

export const salePageChange = (page) => ({
  type: actionTypes.SALE_PAGE_CHANGE, page
})

export const saleRowsPerPageChange = (rowsPerPage) => ({
  type: actionTypes.SALE_ROWS_PER_PAGE_CHANGE, rowsPerPage
})

export const toastAdd = (text, action = 'Dismiss') => ({
  type: actionTypes.TOAST_ADD, toast: { text, action }
})

export const toastDismiss = () => ({
  type: actionTypes.TOAST_DISMISS
})

export const artworkUploadsAdd = (id, file) => ({
  type: actionTypes.ARTWORK_UPLOADS_ADD, id, file
})

export const artworkUploadsShowDetails = (id) => ({
  type: actionTypes.ARTWORK_UPLOADS_SHOW_DETAILS, id
})

export const artworkUploadsHideDetails = () => ({
  type: actionTypes.ARTWORK_UPLOADS_HIDE_DETAILS
})

export const artworkUploadsEmpty = () => ({
  type: actionTypes.ARTWORK_UPLOADS_EMPTY
})

export const artworkUploadsInitProcessing = () => (dispatch) => {
  dispatch({ type: actionTypes.ARTWORK_UPLOADS_INIT_PROCESSING, dispatch })
}

export const artworkUploadsStopProcessing = () => ({
  type: actionTypes.ARTWORK_UPLOADS_STOP_PROCESSING
})

export const artworkFilesReceive = (files, routerHistory) => ({
  type: actionTypes.ARTWORK_FILES_RECEIVE, files, routerHistory
})

export const artworkFilesProcessed = () => ({
  type: actionTypes.ARTWORK_FILES_PROCESSED
})

// async action creators

export const loadAuthUser = () => (dispatch) => {
  dispatch({ type: actionTypes.LOADING_AUTH_USER })
  return Auth.current().then(response => {
    if (response.status === 200) {
      return response.json().then(user => {
        dispatch({
          type: actionTypes.LOADING_AUTH_USER_SUCCEEDED, payload: user })
        return user
      })
    } else {
      dispatch({ type: actionTypes.LOADING_AUTH_USER_FAILED })
    }
  })
}

export const signIn = (email, password, captcha) => (dispatch) => {
  const encp = stringToBase64(password)
  dispatch({ type: actionTypes.SIGNING_IN })
  return Auth.signIn(email, encp, captcha).then(response => {
    if (response.status === 200) {
      return response.json().then(user => {
        dispatch({ type: actionTypes.SIGNING_IN_SUCCEEDED, payload: user })
        return user
      })
    } else {
      return response.json()
        .then(error => {
          dispatch({ type: actionTypes.SIGNING_IN_FAILED })
          dispatch(toastAdd(error.message))
          return error
        })
        .catch(error => {
          dispatch({ type: actionTypes.SIGNING_IN_FAILED })
          if (error) {
            return error
          }
        })
    }
  })
}

export const sendResetInstructions = (email, captcha) => (dispatch) => {
  dispatch({ type: actionTypes.SENDING_RESET_INSTRUCTIONS })
  return Auth.sendResetInstructions(email, captcha).then(response => {
    if (response.status === 200) {
      dispatch({ type: actionTypes.SENDING_RESET_INSTRUCTIONS_SUCCEEDED })
      dispatch(toastAdd('Instructions have been sent to your email address.'))
      return {}
    } else {
      return response.json().then(error => {
        dispatch({ type: actionTypes.SENDING_RESET_INSTRUCTIONS_FAILED })
        dispatch(toastAdd(error.message))
        return error
      })
    }
  })
}

export const resetPassword = (email, token, password) => (dispatch) => {
  const encp = stringToBase64(password)
  dispatch({ type: actionTypes.UPDATING_USER_PASSWORD })
  return Auth.resetPassword(email, token, encp).then(response => {
    if (response.status === 200) {
      dispatch({ type: actionTypes.UPDATING_USER_PASSWORD_SUCCEEDED })
      dispatch(toastAdd('Password updated.'))
      return {}
    } else {
      return response.json().then(error => {
        dispatch({ type: actionTypes.UPDATING_USER_PASSWORD_FAILED })
        dispatch(toastAdd(error.message))
        return error
      })
    }
  })
}

export const signOut = () => (dispatch) => {
  dispatch({ type: actionTypes.SIGNING_OUT })
  return Auth.signOut().then(response => {
    if (response.status === 200) {
      dispatch({ type: actionTypes.SIGNING_OUT_SUCCEEDED })
    } else {
      dispatch({ type: actionTypes.SIGNING_OUT_FAILED })
    }
  })
}

export const signUp = (data) => (dispatch) => {
  const encp = stringToBase64(data.password)
  const captcha = data.captcha
  data = _.omit(data, [ 'password', 'captcha' ])
  dispatch({ type: actionTypes.SIGNING_UP })
  return User.create(data, encp, captcha).then(response => {
    if (response.status === 200) {
      return response.json().then(user => {
        dispatch({ type: actionTypes.SIGNING_UP_SUCCEEDED, payload: user })
        dispatch(toastAdd('Account created. Please sign in.'))
        return user
      })
    } else {
      return response.json().then(error => {
        dispatch({ type: actionTypes.SIGNING_UP_FAILED })
        dispatch(toastAdd(error.message))
        return error
      })
    }
  })
}

export const verifyUser = (email, verificationToken) => (dispatch) => {
  dispatch({ type: actionTypes.VERIFYING_USER })
  return User.verify(email, verificationToken).then(response => {
    if (response.status === 200) {
      return response.json().then(user => {
        dispatch({ type: actionTypes.VERIFYING_USER_SUCCEEDED })
        dispatch(toastAdd('Account verified.'))
        return user
      })
    } else {
      dispatch({ type: actionTypes.VERIFYING_USER_FAILED })
      dispatch(toastAdd('Account verification failed.'))
      return response.json()
    }
  })
}

export const refreshSales = () => (dispatch) => {
  dispatch({ type: actionTypes.REFRESHING_SALES })
  return Sale.refresh().then(response => {
    if (response.status === 200) {
      dispatch({ type: actionTypes.REFRESHING_SALES_SUCCEEDED })
      dispatch(toastAdd('Refreshing all sales in the background.'))
      return {}
    } else {
      return response.json().then(error => {
        dispatch({ type: actionTypes.REFRESHING_SALES_FAILED })
        dispatch(toastAdd(error.message))
        return error
      })
    }
  })
}

export const loadUser = (userId) => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.LOADING_USERS })
  return User.getOne(userId).then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return response.json().then(user => {
        dispatch({
          type: actionTypes.LOADING_USERS_SUCCEEDED,
          payload: normalize(user, schema.user) })
        return user
      })
    } else {
      dispatch({ type: actionTypes.LOADING_USERS_FAILED })
    }
  })
}

export const loadUsers = () => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.LOADING_USERS })
  return User.getAll().then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return response.json().then(users => {
        dispatch({
          type: actionTypes.LOADING_USERS_SUCCEEDED,
          payload: normalize(users, [ schema.user ]) })
        return users
      })
    } else {
      dispatch({ type: actionTypes.LOADING_USERS_FAILED })
    }
  })
}

export const uploadProfileImage = (userId, file) => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.UPLOADING_PROFILE, id: userId })
  return User.uploadImage(userId, file, (progress) => {
    dispatch({
      type: actionTypes.UPLOADING_PROFILE_PROGRESS,
      id: userId,
      progress
    })
  }).then(response => {
    const user = response.response
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return dispatch({
        type: actionTypes.UPLOADING_PROFILE_SUCCEEDED,
        id: user.id,
        payload: normalize(user, schema.user) })
    } else {
      dispatch({ type: actionTypes.UPLOADING_PROFILE_FAILED })
    }
  })
}

export const updateUser = (userId, data, merge = false) => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.UPDATING_USER, payload: data })
  const api = merge ? User.merge : User.update
  return api(userId, data).then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return response.json().then(user => {
        dispatch({
          type: actionTypes.UPDATING_USER_SUCCEEDED,
          payload: normalize(user, schema.user) })
        return user
      })
    } else {
      dispatch({ type: actionTypes.UPDATING_USER_FAILED })
      return response.json()
    }
  })
}

export const mergeUser = (userId, data) =>
  updateUser(userId, data, true)

export const updateUserPassword = (userId, password, currentPassword = null) => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.UPDATING_USER_PASSWORD, payload: userId })
  return User.updatePassword(userId, password, currentPassword).then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return response.json().then(user => {
        dispatch({
          type: actionTypes.UPDATING_USER_PASSWORD_SUCCEEDED,
          payload: user })
        return user
      })
    } else {
      dispatch({ type: actionTypes.UPDATING_USER_PASSWORD_FAILED })
      return response.json()
    }
  })
}

export const loadArtwork = (artworkId) => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.LOADING_ARTWORKS })
  return Artwork.getOne(artworkId).then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return response.json().then(artwork => {
        dispatch({
          type: actionTypes.LOADING_ARTWORKS_SUCCEEDED,
          payload: normalize(artwork, schema.artwork) })
        return artwork
      })
    } else {
      dispatch({ type: actionTypes.LOADING_ARTWORKS_FAILED })
    }
  })
}

export const loadArtworkCount = (status = '*') => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.LOADING_ARTWORK_COUNT })
  return Artwork.getCount(status).then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return response.json().then(result => {
        dispatch({
          type: actionTypes.LOADING_ARTWORK_COUNT_SUCCEEDED,
          count: result.count,
          status
        })
        return result
      })
    } else {
      dispatch({ type: actionTypes.LOADING_ARTWORK_COUNT_FAILED })
    }
  })
}

export const loadArtworks = (start = 0, limit = 10, status = '*') =>
  (dispatch) => {
    dispatch({ type: actionTypes.FETCHING })
    dispatch({ type: actionTypes.LOADING_ARTWORKS })
    return Artwork.getAll(start, limit, status).then(response => {
      dispatch({ type: actionTypes.FETCHING_ENDED })
      if (response.status === 200) {
        return response.json().then(artworks => {
          dispatch({
            type: actionTypes.LOADING_ARTWORKS_SUCCEEDED,
            count: Number(response.headers.get('count')),
            status: status,
            payload: normalize(artworks, [ schema.artwork ]) })
          return artworks
        })
      } else {
        dispatch({ type: actionTypes.LOADING_ARTWORKS_FAILED })
      }
    })
  }

export const createArtwork = (data, file) => async (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.CREATING_ARTWORK, payload: data })

  const response = await Artwork.create(data)

  dispatch({ type: actionTypes.FETCHING_ENDED })

  if (response.ok) {
    const artwork = await response.json()

    dispatch(artworkUploadsAdd(artwork.id, file))

    dispatch({
      type: actionTypes.CREATING_ARTWORK_SUCCEEDED,
      payload: normalize(artwork, schema.artwork)
    })

    return artwork
  } else {
    dispatch({ type: actionTypes.CREATING_ARTWORK_FAILED })
    return response.json()
  }
}

export const uploadArtworkImage = (artworkId, file) => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.UPLOADING_ARTWORK, id: artworkId })
  return Artwork.uploadImage(artworkId, file, (progress) => {
    dispatch({
      type: actionTypes.UPLOADING_ARTWORK_PROGRESS,
      id: artworkId,
      progress
    })
  }).then(response => {
    const artwork = response.response
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return dispatch({
        type: actionTypes.UPLOADING_ARTWORK_SUCCEEDED,
        id: artwork.id,
        payload: normalize(artwork, schema.artwork) })
    } else {
      dispatch({ type: actionTypes.UPLOADING_ARTWORK_FAILED, id: artworkId })
    }
  })
}

export const updateArtwork = (artworkId, data, merge = false) => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.UPDATING_ARTWORK, payload: data })
  const api = merge ? Artwork.merge : Artwork.update
  return api(artworkId, data).then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return response.json().then(artwork => {
        dispatch({
          type: actionTypes.UPDATING_ARTWORK_SUCCEEDED,
          payload: normalize(artwork, schema.artwork) })
        return artwork
      })
    } else {
      dispatch({ type: actionTypes.UPDATING_ARTWORK_FAILED })
      return response.json()
    }
  })
}

export const mergeArtwork = (artworkId, data) =>
  updateArtwork(artworkId, data, true)

export const publishArtwork = (artworkId) => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.PUBLISHING_ARTWORK })
  return Artwork.publish(artworkId).then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return response.json().then(artwork => {
        dispatch({
          type: actionTypes.PUBLISHING_ARTWORK_SUCCEEDED,
          payload: normalize(artwork, schema.artwork) })
        return artwork
      })
    } else {
      dispatch({ type: actionTypes.PUBLISHING_ARTWORK_FAILED })
      return response.json()
    }
  })
}

export const unpublishArtwork = (artworkId) => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.UNPUBLISHING_ARTWORK })
  return Artwork.unpublish(artworkId).then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return response.json().then(artwork => {
        dispatch({
          type: actionTypes.PUBLISHING_ARTWORK_SUCCEEDED,
          payload: normalize(artwork, schema.artwork) })
        return artwork
      })
    } else {
      dispatch({ type: actionTypes.UNPUBLISHING_ARTWORK_FAILED })
      return response.json()
    }
  })
}

export const loadTags = () => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.LOADING_TAGS })
  return Tag.getAll().then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return response.json().then(tags => {
        dispatch({
          type: actionTypes.LOADING_TAGS_SUCCEEDED,
          payload: normalize(tags, [ schema.tag ]) })
        return tags
      })
    } else {
      dispatch({ type: actionTypes.LOADING_TAGS_FAILED })
    }
  })
}

export const deleteArtwork = (artworkId) => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.DELETING_ARTWORK })
  return Artwork.delete(artworkId).then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 204) {
      dispatch({ type: actionTypes.DELETING_ARTWORK_SUCCEEDED, id: artworkId })
      return artworkId
    } else {
      dispatch({ type: actionTypes.DELETING_ARTWORK_FAILED })
      return response.json()
    }
  })
}

export const replicateMarkups = (artworkId, frameType) => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.REPLICATING_MARKUPS })
  return Artwork.replicateMarkups(artworkId, frameType).then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      dispatch({ type: actionTypes.REPLICATING_MARKUPS_SUCCEEDED })
    } else {
      dispatch({ type: actionTypes.REPLICATING_MARKUPS_FAILED })
    }
  })
}

export const loadFrameTypes = () => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.LOADING_FRAMETYPES })
  return FrameType.getAll().then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return response.json().then(frameTypes => {
        dispatch({
          type: actionTypes.LOADING_FRAMETYPES_SUCCEEDED,
          payload: normalize(frameTypes, [ schema.frameType ]) })
        return frameTypes
      })
    } else {
      dispatch({ type: actionTypes.LOADING_FRAMETYPES_FAILED })
    }
  })
}

export const loadSizes = () => (dispatch) => {
  dispatch({ type: actionTypes.FETCHING })
  dispatch({ type: actionTypes.LOADING_SIZES })
  return Size.getAll().then(response => {
    dispatch({ type: actionTypes.FETCHING_ENDED })
    if (response.status === 200) {
      return response.json().then(sizes => {
        dispatch({
          type: actionTypes.LOADING_SIZES_SUCCEEDED,
          payload: normalize(sizes, [ schema.size ]) })
        return sizes
      })
    } else {
      dispatch({ type: actionTypes.LOADING_SIZES_FAILED })
    }
  })
}

export const loadSales = (start = 0, limit = 10) =>
  (dispatch) => {
    dispatch({ type: actionTypes.FETCHING })
    dispatch({ type: actionTypes.LOADING_SALES })
    return Sale.getAll(start, limit).then(response => {
      dispatch({ type: actionTypes.FETCHING_ENDED })
      if (response.status === 200) {
        return response.json().then(sales => {
          dispatch({
            type: actionTypes.LOADING_SALES_SUCCEEDED,
            count: Number(response.headers.get('count')),
            payload: normalize(sales, [ schema.sale ]) })
          return sales
        })
      } else {
        dispatch({ type: actionTypes.LOADING_SALES_FAILED })
      }
    })
  }

export const resetReduxForm = formName => dispatch => dispatch(reset(formName))



// WEBPACK FOOTER //
// ./src/redux/actions/index.js